/**
 * Default stroke / chip colors for the PARA milestone tier icons (1–10).
 * Ordered by increasing gem rarity / prestige (tier 1 = entry, tier 10 = pinnacle).
 *
 * PARA I (1–5): Onyx → Carnelian → Citrine → Moonstone → Pearl
 * PARA II (6–10): Amethyst → Sapphire → Emerald → Ruby → Diamond
 *
 * Used when the API omits `milestone.color`.
 */
export const PARA_MILESTONE_TIER_HEX: readonly string[] = [
	'#171717', // 1 Onyx (Neutral 900)
	'#C2410C', // 2 Carnelian (Orange 700)
	'#EAB308', // 3 Citrine (Yellow 500)
	'#7DD3FC', // 4 Moonstone (Sky 300 — lighter icy sheen)
	'#F472B6', // 5 Pearl (Pink 400 — warm rose/pearl luster)
	'#9333EA', // 6 Amethyst (Purple 600)
	'#2563EB', // 7 Sapphire (Blue 600)
	'#059669', // 8 Emerald (Emerald 600)
	'#DC2626', // 9 Ruby (Red 600)
	'#0284C7', // 10 Diamond (Sky 600)
];

/** Display names for tier icons 1–10 (matches {@link PARA_MILESTONE_TIER_HEX} rarity order). */
export const PARA_MILESTONE_TIER_GEM_NAMES: readonly string[] = [
	'Onyx',
	'Carnelian',
	'Citrine',
	'Moonstone',
	'Pearl',
	'Amethyst',
	'Sapphire',
	'Emerald',
	'Ruby',
	'Diamond',
];

/**
 * Clamp to tier 1–10 for icon selection.
 * Uses `fallback1Based` when the raw sort order is missing or &lt; 1.
 */
export function clampParaMilestoneIconTier(raw: unknown, fallback1Based = 1): number {
	let n = typeof raw === 'number' ? raw : Number(raw);
	if (!Number.isFinite(n) || n < 1) {
		n = fallback1Based;
	}
	const t = Math.floor(n);
	const fb = Number.isFinite(fallback1Based) && fallback1Based >= 1 ? Math.floor(fallback1Based) : 1;
	const safe = Number.isFinite(t) && t >= 1 ? t : fb;
	return Math.min(10, Math.max(1, safe));
}

export function defaultParaMilestoneTierStroke(tier: number): string {
	return PARA_MILESTONE_TIER_HEX[clampParaMilestoneIconTier(tier) - 1] ?? PARA_MILESTONE_TIER_HEX[0];
}

export function defaultParaMilestoneTierGemName(tier: number): string {
	return PARA_MILESTONE_TIER_GEM_NAMES[clampParaMilestoneIconTier(tier) - 1] ?? PARA_MILESTONE_TIER_GEM_NAMES[0];
}
